import { AccessControlCode } from '@/shared/constants';
import { useAppDispatch, useAppSelector } from '@hooks/useRedux';
import {
  selectCurrentUser,
  selectHasAccessControl,
  selectOutletHasKdsEnabled,
  selectUserRoleName,
} from '@store/auth/authSelectors';
import { logout } from '@store/auth/authThunks';
import type { LucideIcon } from 'lucide-react';
import {
  Calculator,
  Hotel,
  LayoutDashboard,
  LogOut,
  Monitor,
  Package2,
  Settings,
  ShoppingCart,
  UserIcon,
} from 'lucide-react';
import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { apiError } from '../../util/notify';
import { BILLING_PAGE_PATH } from '../../util/renderConstants';

interface UserMenuDropDownProps {
  onOpenChange: (isOpen: boolean) => void;
}

interface MenuNavButtonProps {
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  className?: string;
}

const MenuNavButton = ({
  label,
  icon: Icon,
  onClick,
  isActive = false,
  disabled = false,
  className = '',
}: MenuNavButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
      isActive ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : ''
    } ${
      disabled
        ? 'opacity-40 cursor-not-allowed text-slate-400 hover:bg-transparent hover:border-transparent'
        : ''
    } ${className}`.trim()}
  >
    <span className="flex items-center gap-2">
      <Icon size={16} />
      {label}
    </span>
    {isActive && (
      <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
    )}
  </button>
);

const MODULE_PREFIXES: { prefix: string; label: string }[] = [
  { prefix: '/accommodation', label: 'Accommodation' },
  { prefix: '/accounting', label: 'Accounting' },
  { prefix: '/inventory', label: 'Inventory' },
  { prefix: '/settings', label: 'Settings' },
  { prefix: '/dashboard', label: 'Dashboard' },
  { prefix: '/sales', label: 'Sales' },
  { prefix: '/kitchen-display', label: 'Kitchen Display' },
];

export const UserMenuDropDown = ({ onOpenChange }: UserMenuDropDownProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector(selectCurrentUser);
  const formattedRole = useAppSelector(selectUserRoleName);
  const canAccessManagementDashboard = useAppSelector(
    selectHasAccessControl(AccessControlCode.DASHBOARD_PAGE_ACCESS),
  );
  const canAccessSales = useAppSelector(
    selectHasAccessControl(AccessControlCode.SALES_ORDERS_CREATE),
  );
  const canAccessAccommodation = useAppSelector(
    selectHasAccessControl(AccessControlCode.ACCOMMODATION_PAGE_ACCESS),
  );
  const canAccessAccounting = useAppSelector(
    selectHasAccessControl(AccessControlCode.ACCOUNTING_VIEW_REPORTS),
  );
  const canAccessInventory = useAppSelector(
    selectHasAccessControl(AccessControlCode.INVENTORY_PAGE_ACCESS),
  );
  const canAccessSettingsHome = useAppSelector(
    selectHasAccessControl(AccessControlCode.SETTINGS_PAGE_ACCESS),
  );
  // Printers and Billing live under Settings, so users who can manage either
  // need the entry point even without general settings access.
  const canManagePrinters = useAppSelector(
    selectHasAccessControl(AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE),
  );
  const canAccessBilling = useAppSelector(
    selectHasAccessControl(AccessControlCode.DASHBOARD_BILLINGS_ACCESS),
  );
  const canAccessSettings =
    canAccessSettingsHome || canManagePrinters || canAccessBilling;
  let settingsEntryPath = '/settings/';
  if (!canAccessSettingsHome) {
    settingsEntryPath = canAccessBilling
      ? BILLING_PAGE_PATH
      : '/settings/printers/all';
  }
  const canAccessKds = useAppSelector(
    selectHasAccessControl(AccessControlCode.DASHBOARD_KDS_ACCESS),
  );
  const outletHasKds = useAppSelector(selectOutletHasKdsEnabled);

  const userName = useMemo(() => {
    if (!currentUser) return 'User';
    return `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
  }, [currentUser]);

  const activeModule = useMemo(() => {
    const match = MODULE_PREFIXES.find((m) =>
      location.pathname.startsWith(m.prefix),
    );
    return match?.label ?? null;
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      onOpenChange(false);
      navigate('/');
    } catch (err) {
      const errMsg = typeof err === 'string' ? err : 'Failed to sign out.';
      apiError(errMsg);
    }
  };

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <div className="absolute right-0 top-11 z-50 w-72 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.12)]">
      <div className="flex flex-row gap-1 items-center border-b border-slate-100 pb-3">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-emerald-700 text-white shrink-0">
          <UserIcon size={32} />
        </span>
        <div className="flex flex-col min-w-0">
          <span
            className="text-sm font-semibold max-w-[160px] truncate"
            title={userName}
          >
            {userName}
          </span>
          <span
            className="text-sm text-slate-600 truncate max-w-[160px]"
            title={currentUser?.email}
          >
            {currentUser?.email || 'No email available'}
          </span>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="inline-flex rounded-full w-fit bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
              {formattedRole}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {canAccessSales && (
          <MenuNavButton
            label="Sales"
            icon={ShoppingCart}
            isActive={activeModule === 'Sales'}
            onClick={() => go('/sales')}
            className="hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          />
        )}

        {canAccessKds && outletHasKds && (
          <MenuNavButton
            label="KDS"
            icon={Monitor}
            isActive={activeModule === 'Kitchen Display'}
            onClick={() => go('/kitchen-display')}
            className="hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          />
        )}

        {canAccessManagementDashboard && (
          <MenuNavButton
            label="Sales Dashboard"
            icon={LayoutDashboard}
            isActive={activeModule === 'Dashboard'}
            onClick={() => go('/dashboard')}
            className="hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700"
          />
        )}

        {canAccessAccommodation && (
          <MenuNavButton
            label="Accommodation"
            icon={Hotel}
            isActive={activeModule === 'Accommodation'}
            onClick={() => go('/accommodation/')}
            className="hover:border-pos-primary/20 hover:bg-pos-primary/5 hover:text-pos-primary"
          />
        )}

        {canAccessAccounting && (
          <MenuNavButton
            label="Accounting"
            icon={Calculator}
            isActive={activeModule === 'Accounting'}
            onClick={() => go('/accounting/')}
            className="hover:border-pos-primary/20 hover:bg-pos-primary/5 hover:text-pos-primary"
          />
        )}

        {canAccessInventory && (
          <MenuNavButton
            label="Inventory"
            icon={Package2}
            isActive={activeModule === 'Inventory'}
            onClick={() => go('/inventory/')}
            className="hover:border-pos-primary/20 hover:bg-pos-primary/5 hover:text-pos-primary"
          />
        )}

        {canAccessSettings && (
          <MenuNavButton
            label="Settings"
            icon={Settings}
            isActive={activeModule === 'Settings'}
            onClick={() => go(settingsEntryPath)}
            className="hover:border-pos-primary/20 hover:bg-pos-primary/5 hover:text-pos-primary"
          />
        )}

        <MenuNavButton
          label="Logout"
          icon={LogOut}
          onClick={handleLogout}
          className="hover:bg-rose-50/50"
        />
      </div>
    </div>
  );
};
